import React, { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { Alert, Col, Row, Slider, Table, Tooltip as AntdTooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import moment from 'moment';
import {
	MAX_SUPPLIERS_IN_TABLE,
	PERFORMER_GROUP,
	PERFORMER_GROUP_ORDER,
	CHART_PRIMARY_COLOR,
} from './LifeCycleAnalyticsConstants';
import {
	COMPARISON_MODE,
	COMPARISON_SERIES_FIELD,
	COMPARISON_SERIES_CURRENT,
	COMPARISON_SERIES_PREVIOUS,
	getComparisonCurrentColor,
	getComparisonPreviousColor,
} from '../../utils/AnalyticsComparisonUtils';
import ComparisonLegend from '../analytics_comparison/ComparisonLegend';
import { drawTopPerformerStarSymbol } from './registerTopPerformerShape';
import {
	AnalyticsChartActions,
	AnalyticsChartCard,
	AnalyticsChartHeader,
	PerformanceScatterChart,
} from '../analytics_charts';

const WO_PERFORMANCE_TABLE_SCROLL_Y = 390;

const WoPerformanceSection = ({
	chartColor,
	filteredSupplierPerformance,
	previousSupplierPerformance,
	supplierTableColumns,
	selectedPriorityCard,
	minWoCount,
	setMinWoCount,
	formatDurationAxisLabel,
	formatPerformerShape,
	formatPerformerSize,
	formatPerformerStyle,
	buildPerformanceTooltip,
	handleWoPerformancePointClick,
	exportSupplierPerformanceCsv,
	comparisonState,
	selectedRange,
	comparisonRange,
}) => {
	const effectiveChartColor = chartColor || CHART_PRIMARY_COLOR;
	const comparisonMode = comparisonState ? comparisonState.mode : COMPARISON_MODE.NONE;
	const hasComparison =
		comparisonMode !== COMPARISON_MODE.NONE &&
		previousSupplierPerformance &&
		previousSupplierPerformance.length > 0;

	const currentData = (filteredSupplierPerformance || [])
		.slice(0, MAX_SUPPLIERS_IN_TABLE)
		.map((row) => ({ ...row, [COMPARISON_SERIES_FIELD]: COMPARISON_SERIES_CURRENT }));

	const previousData = hasComparison
		? (previousSupplierPerformance || [])
				.slice(0, MAX_SUPPLIERS_IN_TABLE)
				.map((row) => ({ ...row, [COMPARISON_SERIES_FIELD]: COMPARISON_SERIES_PREVIOUS }))
		: [];

	const chartData = hasComparison ? [...currentData, ...previousData] : currentData;

	const selectedStart: moment.Moment | null = selectedRange ? selectedRange[0] : null;
	const selectedEnd: moment.Moment | null = selectedRange ? selectedRange[1] : null;
	const comparisonStart: moment.Moment | null = comparisonRange ? comparisonRange[0] : null;
	const comparisonEnd: moment.Moment | null = comparisonRange ? comparisonRange[1] : null;

	const [tableScrollYDisabledForPrint, setTableScrollYDisabledForPrint] = useState(false);

	useEffect(() => {
		if (typeof window === 'undefined') {
			return undefined;
		}
		const onBeforePrint = () => {
			flushSync(() => {
				setTableScrollYDisabledForPrint(true);
			});
		};
		const onAfterPrint = () => {
			setTableScrollYDisabledForPrint(false);
		};
		window.addEventListener('beforeprint', onBeforePrint);
		window.addEventListener('afterprint', onAfterPrint);
		const mq = window.matchMedia ? window.matchMedia('print') : null;
		const onPrintMq = () => {
			if (mq && mq.matches) {
				flushSync(() => {
					setTableScrollYDisabledForPrint(true);
				});
			} else {
				setTableScrollYDisabledForPrint(false);
			}
		};
		if (mq && mq.addEventListener) {
			mq.addEventListener('change', onPrintMq);
		}
		return () => {
			window.removeEventListener('beforeprint', onBeforePrint);
			window.removeEventListener('afterprint', onAfterPrint);
			if (mq && mq.removeEventListener) {
				mq.removeEventListener('change', onPrintMq);
			}
		};
	}, []);

	const headerPill = selectedPriorityCard
		? {
				label: selectedPriorityCard.woPriorityName,
				color: selectedPriorityCard.color,
				bgColor: selectedPriorityCard.bgColor,
				maxWidth: 250,
		  }
		: null;

	const sliderChip = (
		<div className="box-border inline-flex shrink-0 items-center gap-2 whitespace-nowrap rounded-[2px] border-0 border-b border-solid border-grey-5 bg-grey-2 px-3 py-1 print:!hidden">
			<AntdTooltip title="Filter suppliers by minimum number of completed work orders">
				<InfoCircleOutlined className="text-sm text-character-secondary" />
			</AntdTooltip>
			<span className="footnoteregular mr-1 shrink-0 text-gray-500">
				Minimum WOs completed by supplier:
			</span>
			<div className="flex shrink-0 items-center">
				<Slider
					min={0}
					max={1000}
					step={10}
					value={minWoCount}
					onChange={setMinWoCount}
					className="woPerformanceMinWoSlider !m-0 w-[120px] shrink-0"
					trackStyle={{ backgroundColor: effectiveChartColor }}
					handleStyle={{ borderColor: effectiveChartColor }}
				/>
			</div>
			<span className="min-w-8 shrink-0 whitespace-nowrap text-right text-xs font-normal leading-none text-grey-8">
				{minWoCount}
			</span>
		</div>
	);

	const title = (
		<AnalyticsChartHeader pill={headerPill} label="WO Performance" after={sliderChip} />
	);

	// TODO: WO Performance comparison dropdown — re-enable in a future phase by passing
	// a `comparison={...}` prop to AnalyticsChartActions (state, range, and loading are
	// already plumbed through props). Hidden for now while the scatter-plot comparison
	// visuals/UX are finalized; the comparison legend still renders when `hasComparison`
	// is true so URL-driven comparisons remain visible.
	const extra = <AnalyticsChartActions onDownload={exportSupplierPerformanceCsv} />;

	const pointColor = hasComparison
		? [
				COMPARISON_SERIES_FIELD,
				(series: any) =>
					series === COMPARISON_SERIES_CURRENT
						? getComparisonCurrentColor()
						: getComparisonPreviousColor(),
		  ]
		: effectiveChartColor;

	const pointShape = hasComparison
		? [
				`${COMPARISON_SERIES_FIELD}*performerGroup*isInternalTeam`,
				(series: any, group: any, isInternal: any) =>
					series === COMPARISON_SERIES_CURRENT
						? formatPerformerShape(group, isInternal)
						: 'hollowCircle',
		  ]
		: ['performerGroup*isInternalTeam', formatPerformerShape];

	const pointSize = hasComparison
		? [
				`${COMPARISON_SERIES_FIELD}*performerGroup*isInternalTeam`,
				(series: any, group: any, isInternal: any) =>
					series === COMPARISON_SERIES_CURRENT ? formatPerformerSize(group, isInternal) : 4,
		  ]
		: ['performerGroup*isInternalTeam', formatPerformerSize];

	const pointStyle = hasComparison
		? [
				`${COMPARISON_SERIES_FIELD}*performerGroup*isInternalTeam`,
				(series: any, group: any, isInternal: any) =>
					series === COMPARISON_SERIES_CURRENT ? formatPerformerStyle(group, isInternal) : {},
		  ]
		: ['performerGroup*isInternalTeam', formatPerformerStyle];

	const customLegendItems = hasComparison
		? [
				{
					value: PERFORMER_GROUP.TOP,
					name: PERFORMER_GROUP.TOP,
					marker: {
						symbol: drawTopPerformerStarSymbol,
						style: { fill: effectiveChartColor, r: 5 },
					},
				},
				{
					value: PERFORMER_GROUP.TYPICAL,
					name: PERFORMER_GROUP.TYPICAL,
					marker: {
						symbol: 'circle',
						style: {
							stroke: effectiveChartColor,
							fill: 'transparent',
							lineWidth: 1.5,
							r: 4,
						},
					},
				},
				{
					value: PERFORMER_GROUP.BOTTOM,
					name: PERFORMER_GROUP.BOTTOM,
					marker: {
						symbol: 'cross',
						style: {
							stroke: effectiveChartColor,
							lineWidth: 1.5,
							r: 4,
						},
					},
				},
		  ]
		: undefined;

	return (
		<AnalyticsChartCard variant="flush" title={title} extra={extra}>
			<Row
				gutter={0}
				className="woPerformanceChartRow print:!m-0 print:!flex print:!w-full print:!max-w-full print:!flex-col print:!flex-nowrap print:!items-center"
			>
				<Col
					span={12}
					className="border-0 border-r border-solid border-grey-5 print:!order-1 print:!w-full print:!max-w-[920px] print:!flex-none print:!basis-auto print:!self-center print:!border-r-0 print:!px-3 print:!pb-4"
				>
					{filteredSupplierPerformance.length > MAX_SUPPLIERS_IN_TABLE && (
						<Alert
							className="!mb-0 !rounded-none !border-none bg-character-dangerBg"
							type="error"
							banner
							showIcon={false}
							closable={false}
							message={`Too much data to display. showing ${MAX_SUPPLIERS_IN_TABLE}/${filteredSupplierPerformance.length} suppliers. Filter to narrow selection`}
						/>
					)}
					<div className="w-full min-w-0 print:!flex print:!break-inside-avoid print:!justify-center">
						<PerformanceScatterChart
							data={chartData}
							xField="avgResponseTimeMinutes"
							yField="avgResolutionMinutes"
							xAxisTitle="Avg. Response Time"
							yAxisTitle="Avg. Resolution Time"
							formatAxisLabel={formatDurationAxisLabel}
							pointColor={pointColor}
							pointShape={pointShape}
							pointSize={pointSize}
							pointStyle={pointStyle}
							pointTooltipDefinition={[
								'supplierFacilityName*woCount*avgResponseTimeSeconds*avgResolutionTimeSeconds',
								buildPerformanceTooltip,
							]}
							onPointClick={handleWoPerformancePointClick}
							scale={{
								avgResponseTimeMinutes: { nice: true },
								avgResolutionMinutes: { nice: true },
								performerGroup: { values: PERFORMER_GROUP_ORDER },
							}}
							customLegendItems={customLegendItems}
						/>
						{hasComparison && (
							<ComparisonLegend
								currentStart={selectedStart}
								currentEnd={selectedEnd}
								previousStart={comparisonStart}
								previousEnd={comparisonEnd}
								currentColor={getComparisonCurrentColor()}
								previousColor={getComparisonPreviousColor()}
								mode={comparisonMode}
							/>
						)}
					</div>
				</Col>
				<Col
					span={12}
					className="print:!order-2 print:!w-full print:!max-w-[920px] print:!flex-none print:!basis-auto print:!self-center print:!border-b-0 print:!border-l-0 print:!border-r-0 print:!border-t print:!border-solid print:!border-grey-5 print:!px-3 print:!pt-4"
				>
					<Table
						className="w-full"
						columns={supplierTableColumns}
						rowKey={(row) => `${row.supplierFacilityId}`}
						pagination={false}
						size="small"
						scroll={tableScrollYDisabledForPrint ? undefined : { y: WO_PERFORMANCE_TABLE_SCROLL_Y }}
						dataSource={filteredSupplierPerformance.slice(0, MAX_SUPPLIERS_IN_TABLE)}
					/>
				</Col>
			</Row>
		</AnalyticsChartCard>
	);
};

export default WoPerformanceSection;
